'use client';

import type { Dispatch, SetStateAction } from 'react';
import type { UriType } from '~/model/GlobalTypes';
import { VpButton, VpIcon } from '@vtmn-play/react/headless';
import classNames from 'classnames';
import { useLocale, useTranslations } from 'next-intl';
import { useState } from 'react';
import { fetchItem } from '@/app/actions/server';
import { AcceptLanguage } from '~/const/locale';
import {
  RequestStatus,
  useRequestState,
} from '~/hooks/request/useRequestState';
import { useApiErrorContext } from '../../Provider';
import { StarInput } from './StarInput';

export const StarRatingInput = ({
  isSBreakpoint,
  entityUri,
  setIsRatingOpen,
}: {
  isSBreakpoint: boolean;
  entityUri: UriType;
  setIsRatingOpen: Dispatch<SetStateAction<boolean>>;
}) => {
  const locale = useLocale();
  const t = useTranslations();
  const [rate, setRate] = useState<number>(null);

  const { setError } = useApiErrorContext();
  const { status, startRequest, handleSuccess, handleError }
    = useRequestState();

  const handleSubmit = async () => {
    try {
      startRequest();
      const response = await fetchItem({
        pathname: 'translation/rating',
        headers: {
          acceptLanguage: AcceptLanguage[locale],
        },
        method: 'POST',
        revalidate: 0,
        body: { rate, iri: entityUri },
      });

      if (response['hydra:description']) {
        handleError();
        return setError(response);
      }

      handleSuccess();
      setTimeout(() => {
        setIsRatingOpen(false);
      }, 1200);
    } catch {
      handleError();
    }
  };

  return (
    <>
      <div className="control-rating__support translate__rating__stars">
        <div className="vtmn-rating vtmn-rating_size--medium">
          <div className="vtmn-rating--interactive" role="radiogroup">
            {Array.from({ length: 5 }, (_, index) => index + 1).map(
              (index: number) => (
                <StarInput
                  key={`rating-${index}`}
                  index={index}
                  value={rate}
                  onValueChange={setRate}
                />
              ),
            )}
          </div>
        </div>
      </div>
      {status === RequestStatus.SUCCESS
        ? (
            <div className="translate__rating__success">
              <span
                className={classNames('translate__rating__success-message', {
                  'vp-title-s': isSBreakpoint,
                  'vp-body-m': !isSBreakpoint,
                })}
                data-testid="success-message"
              >
                {t('CONTACT_DIALOG.TITLE')}
              </span>
              <VpIcon name="check-circle" size={isSBreakpoint ? 32 : 24} />
            </div>
          )
        : (
            <VpButton
              className="translate__rating__submit-action"
              disabled={!rate || status === RequestStatus.PENDING}
              size="small"
              onClick={() => handleSubmit()}
            >
              {t('GLOBAL.TRANSLATION_RATING.ACTION')}
            </VpButton>
          )}
    </>
  );
};
